import { useCallback, useEffect, useRef } from "react";
import { Device, Call } from "@twilio/voice-sdk";
import { useCallingStore } from "../../store";
import { useAuth } from "@tellescope/tanstack-query";
import { useMutation } from "@tanstack/react-query";
import { useAudioWithAutoplayHandling } from "../useAudioWithAutoplayHandling";
import { INBOUND_CALL_RING_SRC } from "../../constants";

// Token refresh interval (50 minutes - before 60 minute expiry)
const TOKEN_REFRESH_INTERVAL_MS = 50 * 60 * 1000;

/**
 * Hook for managing Twilio Device initialization, event handling, and token refresh.
 *
 * Responsibilities:
 * - Initialize Twilio Device with auth token from backend
 * - Manage device event listeners (registered, unregistered, error, tokenWillExpire, incoming)
 * - Handle token refresh (interval-based + on tokenWillExpire event)
 * - Auto-reconnection on connection loss
 *
 * Note: Twilio's built-in incoming ring sound is disabled on device init so that we can
 * manage the ring ourselves — giving us control over autoplay error handling.
 */
export function useTwilioDevice() {
  const { session, currentUser } = useAuth();
  const callingStore = useCallingStore();
  const tokenRefreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { playAudio } = useAudioWithAutoplayHandling();
  const ringAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const audio = new Audio(INBOUND_CALL_RING_SRC);
    audio.loop = true;
    audio.preload = "auto";
    ringAudioRef.current = audio;

    callingStore.registerStopIncomingRing(() => {
      audio.pause();
      audio.currentTime = 0;
    });

    return () => {
      audio.pause();
      ringAudioRef.current = null;
    };
    // Intentionally mount-only: callingStore is a stable Zustand reference that never changes,
    // and re-running this effect would create a new Audio instance and a new closure over it,
    // breaking the registered stopRing callback which must reference the same audio object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get store actions directly from the store to avoid stale closures
  const getCallingActions = useCallback(() => {
    return {
      setDevice: callingStore.setDevice,
      setDeviceState: callingStore.setDeviceState,
      setConnectionError: callingStore.setConnectionError,
      addIncomingCall: callingStore.addIncomingCall,
      removeIncomingCall: callingStore.removeIncomingCall,
    };
  }, [
    callingStore.addIncomingCall,
    callingStore.removeIncomingCall,
    callingStore.setConnectionError,
    callingStore.setDevice,
    callingStore.setDeviceState,
  ]);

  /**
   * Fetches a new Twilio access token from the backend
   */
  const fetchAccessToken = useCallback(async (): Promise<string | null> => {
    if (!session) {
      console.warn("[Calling:Device] No session available for token fetch");
      return null;
    }

    try {
      const response = await session.api.phone_calls.authenticate_calling({
        type:
          currentUser.callRouting === "All" || currentUser.callRouting === "Unassigned"
            ? currentUser.callRouting
            : undefined,
      });

      return response.accessToken;
    } catch (error) {
      console.error("[Calling:Device] Failed to fetch access token:", error);
      return null;
    }
  }, [session, currentUser.callRouting]);

  /**
   * Handles incoming call setup - adds listeners and updates store
   */
  const handleIncomingCall = useCallback(
    (incoming: Call) => {
      const actions = getCallingActions();

      playAudio(ringAudioRef.current);

      const stopRing = () => {
        ringAudioRef.current?.pause();
        if (ringAudioRef.current) ringAudioRef.current.currentTime = 0;
      };

      // Handle call disconnect/cancel - remove from queue and stop ring
      const handleDisconnect = () => {
        stopRing();
        actions.removeIncomingCall(incoming);
      };

      incoming.on("disconnect", handleDisconnect);
      incoming.on("cancel", handleDisconnect);
      incoming.on("accept", stopRing);

      // Add to incoming calls queue
      actions.addIncomingCall(incoming);

      console.log("[Calling:Device] Incoming call received:", {
        to: incoming.parameters?.To,
        enduserId: incoming.customParameters?.get("eId"),
        conferenceId: incoming.customParameters?.get("cId"),
      });
    },
    [getCallingActions, playAudio]
  );

  /**
   * Refreshes the Twilio access token
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!callingStore.device) return false;

    const accessToken = await fetchAccessToken();
    if (!accessToken) return false;

    try {
      callingStore.device.updateToken(accessToken);
      console.log("[Calling:Device] Token refreshed successfully");
      return true;
    } catch (error) {
      console.error("[Calling:Device] Failed to update token:", error);
      return false;
    }
  }, [callingStore.device, fetchAccessToken]);

  /**
   * Initializes the Twilio Device - wrapped in a mutation for proper async handling
   */
  const initializeDeviceFn = useCallback(async (): Promise<Device | null> => {
    // Guard: Prevent multiple initialization attempts
    const existingDevice = callingStore.device;
    const currentState = callingStore.deviceState;
    if (existingDevice || currentState === "registering") {
      console.log("[Calling:Device] Device already exists or is registering, skipping initialization");
      return existingDevice;
    }

    // Check if user has calling permissions
    // Use session.userInfo which has the full user info including org properties
    const userInfo = session?.userInfo;
    const twilioNumber = userInfo?.twilioNumber || userInfo?.orgTwilioNumber;
    if (!twilioNumber) {
      console.log("[Calling:Device] No Twilio number configured");
      return null;
    }

    // Check access permissions if available (access.phone_calls.create can be string or null)
    // If null or undefined, assume permission is granted; if set, it should be truthy
    const callAccess = userInfo?.access?.phone_calls?.create;
    if (callAccess !== undefined && callAccess !== null && !callAccess) {
      console.log("[Calling:Device] User lacks phone_calls.create permission");
      return null;
    }

    const actions = getCallingActions();
    actions.setDeviceState("registering");

    // Fetch access token
    const accessToken = await fetchAccessToken();
    if (!accessToken) {
      actions.setConnectionError("Failed to authenticate for calling");
      actions.setDeviceState("unregistered"); // Reset state so retry is possible
      return null;
    }

    // Initialize Twilio Device
    const device = new Device(accessToken, {
      logLevel: 1,
      codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
      closeProtection: true,
    });

    // Disable Twilio's built-in ring so we manage it ourselves (for autoplay error handling)
    device.audio?.incoming(false);

    // Set up event listeners
    device.on("registered", () => {
      console.log("[Calling:Device] Device registered");
      actions.setDeviceState("registered");
    });

    device.on("unregistered", () => {
      console.log("[Calling:Device] Device unregistered");
      const currentDeviceState = callingStore.deviceState;
      // Only set error if we were previously registered (not during cleanup)
      if (currentDeviceState === "registered") {
        actions.setDeviceState("unregistered");
        // Attempt to re-register
        device.register().catch((err) => {
          console.error("[Calling:Device] Failed to re-register:", err);
        });
      }
    });

    device.on("error", (error: { message: string; code?: number }) => {
      console.error("[Calling:Device] Device error:", error);
      actions.setConnectionError(error.message);
    });

    device.on("tokenWillExpire", async () => {
      console.log("[Calling:Device] Token will expire, refreshing...");
      await refreshToken();
    });

    device.on("incoming", handleIncomingCall);

    // Register the device
    try {
      device.register();
      actions.setDevice(device);

      // Start token refresh interval
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
      tokenRefreshIntervalRef.current = setInterval(refreshToken, TOKEN_REFRESH_INTERVAL_MS);

      console.log("[Calling:Device] Device initialized successfully");
      return device;
    } catch (error) {
      console.error("[Calling:Device] Failed to register device:", error);
      actions.setConnectionError("Failed to register calling device");
      actions.setDeviceState("unregistered"); // Reset state so retry is possible
      return null;
    }
  }, [session, fetchAccessToken, getCallingActions, handleIncomingCall, refreshToken]);

  /**
   * TanStack Query mutation for device initialization
   */
  const initializeDeviceMutation = useMutation({
    mutationFn: initializeDeviceFn,
    mutationKey: ["twilio-device-init"],
  });

  /**
   * Reconnects the Twilio Device (e.g., after network loss)
   */
  const reconnect = useCallback(async (): Promise<boolean> => {
    const device = callingStore.device;
    if (!device) {
      // If no device, initialize fresh
      const newDevice = await initializeDeviceFn();
      return newDevice !== null;
    }

    // If device exists but is in registering state, wait
    if (device.state === Device.State.Registering) {
      console.log("[Calling:Device] Device already registering");
      return true;
    }

    const actions = getCallingActions();
    actions.setDeviceState("registering");

    // Refresh token and re-register
    const accessToken = await fetchAccessToken();
    if (!accessToken) {
      actions.setConnectionError("Failed to authenticate for reconnection");
      return false;
    }

    try {
      device.updateToken(accessToken);
      device.register();
      console.log("[Calling:Device] Reconnection initiated");
      return true;
    } catch (error) {
      console.error("[Calling:Device] Reconnection failed:", error);
      actions.setConnectionError("Failed to reconnect");
      return false;
    }
  }, [fetchAccessToken, getCallingActions, initializeDeviceFn]);

  /**
   * Destroys the Twilio Device and cleans up resources
   */
  const destroyDevice = useCallback(() => {
    const device = callingStore.device;

    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current);
      tokenRefreshIntervalRef.current = null;
    }

    if (device) {
      device.destroy();
      const actions = getCallingActions();
      actions.setDevice(null);
      actions.setDeviceState("unregistered");
      console.log("[Calling:Device] Device destroyed");
    }
  }, [getCallingActions]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current);
      }
    };
  }, []);

  return {
    // State
    device: callingStore.device,
    deviceState: callingStore.deviceState,
    connectionError: callingStore.connectionError,
    isConnected: callingStore.deviceState === "registered",
    isInitialized: callingStore.device !== null,

    // Actions
    initializeDevice: initializeDeviceMutation.mutateAsync,
    isInitializing: initializeDeviceMutation.isPending,
    reconnect,
    destroyDevice,
    refreshToken,
  };
}
